import React from 'react';
import { formatCurrency } from '../../ProgramForm/helpers/quotationCalculations';
import { formatDate } from '../../../../utils/programs/dateCalculations';
import { groupInvoiceLines } from '../../../../utils/programs/invoiceGrouping';

const PrintInvoice = ({ invoice, program, lang = 'en', logoUrl = null }) => {
  const isAr = lang === 'ar';

  const t = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field.en || field.ar || '';
  };

  const getPrice = (value) => formatCurrency(value || 0);
  const effectiveCurrency = invoice?.effective_currency || invoice?.invoice_currency || {};
  const currencySymbol = effectiveCurrency.symbol || (effectiveCurrency.code === 'USD' ? '$' : effectiveCurrency.code === 'EUR' ? '€' : (effectiveCurrency.code === 'SAR' ? 'ر.س' : '฿'));

  const amounts = invoice?.display_amounts || {
    subtotal: invoice?.subtotal,
    discount_amount: invoice?.discount_amount,
    price_before_tax: invoice?.price_before_tax,
    tax_amount: invoice?.tax_amount,
    total_due: invoice?.total_due,
  };

  const discountAmount = amounts.discount_amount || 0;
  const taxAmount = amounts.tax_amount || 0;

  const isAgentInvoice = invoice?.invoice_type === 'agent';
  const agentName = t(invoice?.billed_to_name) || t(program?.agent?.name) || t(program?.agent_name) || '-';
  const customerName = t(invoice?.billed_to_customer) || (typeof invoice?.billed_to_customer === 'string' ? invoice.billed_to_customer : '') || (invoice?.invoice_type === 'customer' ? t(invoice?.billed_to_name) : '') || t(program?.description) || '-';

  // Compute pax details with 2-letter code and quantity (e.g. AD 2, CH 1, IN 1)
  const getPaxCode = (nameStr = '') => {
    const lower = nameStr.toLowerCase();
    if (lower.includes('adult') || lower.includes('بالغ')) return 'AD';
    if (lower.includes('child') || lower.includes('طفل') || lower.includes('أطفال')) return 'CH';
    if (lower.includes('infant') || lower.includes('رضيع')) return 'IN';
    if (lower.includes('teen') || lower.includes('مراهق')) return 'TN';
    if (lower.includes('senior') || lower.includes('مسن')) return 'SR';
    // Fallback: take first 2 letters
    const clean = nameStr.replace(/[^a-zA-Z]/g, '').toUpperCase();
    return clean.slice(0, 2) || 'PX';
  };

  let formattedPax = '';
  if (invoice?.pax_breakdown && Array.isArray(invoice.pax_breakdown) && invoice.pax_breakdown.length > 0) {
    formattedPax = invoice.pax_breakdown
      .filter(p => (p.quantity || 0) > 0)
      .map(p => {
        const nameEn = p.name?.en || p.name || (typeof p.pax_type_name === 'string' ? p.pax_type_name : p.pax_type_name?.en) || '';
        const nameAr = p.name?.ar || (typeof p.pax_type_name === 'object' ? p.pax_type_name?.ar : '') || '';
        const code = getPaxCode(nameEn || nameAr);
        return `${code} ${p.quantity}`;
      })
      .join(' - ');
  } else if (program?.pax_types && Array.isArray(program.pax_types) && program.pax_types.length > 0) {
    formattedPax = program.pax_types
      .filter(p => (p.quantity || p.count || 0) > 0)
      .map(p => {
        const nameEn = p.name?.en || (typeof p.name === 'string' ? p.name : '') || p.pax_type_name?.en || '';
        const nameAr = p.name?.ar || p.pax_type_name?.ar || '';
        const code = getPaxCode(nameEn || nameAr);
        return `${code} ${p.quantity || p.count}`;
      })
      .join(' - ');
  } else if (program?.adults_count !== undefined) {
    const parts = [];
    if (program.adults_count > 0) parts.push(`AD ${program.adults_count}`);
    if (program.children_count > 0) parts.push(`CH ${program.children_count}`);
    if (program.infants_count > 0) parts.push(`IN ${program.infants_count}`);
    formattedPax = parts.join(' - ');
  } else if (invoice?.lines?.length > 0) {
    formattedPax = `PAX ${invoice.lines[0]?.quantity || 0}`;
  }

  const paymentStatus = (invoice?.state === 'settled' || invoice?.is_settled) ? (isAr ? 'مدفوعة' : 'Paid') : (isAr ? 'غير مدفوعة' : 'Unpaid');

  return (
    <div className="invoice-container" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="invoice-header">
        <div className="header-left">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="company-logo" />
          ) : (
            <div style={{ padding: '20px', fontSize: '24px', fontWeight: 'bold', color: 'var(--primary)' }}>
              {isAr ? 'ترافل هوليديز' : 'TRIP PLUS TRAVEL'}
            </div>
          )}
        </div>
        <div className="header-right">
          <h2 className="invoice-title">{isAr ? 'فاتورة' : 'INVOICE'} ✈</h2>
        </div>
      </div>

      {/* Info Table */}
      <table className="info-table">
        <tbody>
          {(isAgentInvoice || (agentName && agentName !== '-')) && (
            <tr>
              <th>{isAr ? 'اسم الوكيل' : 'AGENT NAME'}</th>
              <td colSpan="3">{agentName}</td>
            </tr>
          )}
          <tr>
            <th>{isAr ? 'اسم العميل' : 'CUSTOMER NAME'}</th>
            <td>{customerName}</td>
            <th>{isAr ? 'رقم الفاتورة' : 'INVOICE NO.'}</th>
            <td>{invoice?.invoice_number || '-'}</td>
          </tr>
          <tr>
            <th>{isAr ? 'عدد الأفراد' : 'NO. OF PAX'}</th>
            <td>{formattedPax || '-'}</td>
            <th>{isAr ? 'العملة' : 'CURRENCY'}</th>
            <td>{currencySymbol}</td>
          </tr>
          <tr>
            <th>{isAr ? 'رقم الطلب' : 'REQUEST NO.'}</th>
            <td>{program?.request_number || '-'}</td>
            <th>{isAr ? 'رقم التأكيد' : 'CONFIRMATION NO.'}</th>
            <td>{invoice?.confirmation_number || program?.confirmation_number || ''}</td>
          </tr>
          <tr>
            <th>{isAr ? 'تاريخ الإصدار' : 'ISSUED AT'}</th>
            <td>{formatDate(invoice?.issued_at, lang) || '-'}</td>
            <th>{isAr ? 'تاريخ الاستحقاق' : 'PAYMENT DUE BY'}</th>
            <td>{formatDate(invoice?.payment_due_by, lang) || '-'}</td>
          </tr>
          <tr>
            <th>{isAr ? 'تاريخ المغادرة' : 'DEPARTURE DATE'}</th>
            <td>{formatDate(invoice?.departure_date || program?.start_date, lang) || '-'}</td>
            <th>{isAr ? 'تاريخ الوصول' : 'ARRIVAL DATE'}</th>
            <td>{formatDate(invoice?.arrival_date || program?.end_date, lang) || '-'}</td>
          </tr>
        </tbody>
      </table>

      {/* Items Table */}
      <table className="items-table">
        <thead>
          <tr>
            <th style={{ width: '10%' }}>{isAr ? 'الكمية' : 'QUANTITY'}</th>
            <th style={{ width: '56%' }}>{isAr ? 'التفاصيل' : 'DETAILS'}</th>
            <th style={{ width: '16%' }}>{isAr ? 'سعر الوحدة' : 'UNIT PRICE'}</th>
            <th style={{ width: '18%' }}>{isAr ? 'المجموع' : 'TOTAL'}</th>
          </tr>
        </thead>
        <tbody>
          {invoice?.lines?.length > 0 ? (
            groupInvoiceLines(invoice.lines, lang).map((line, idx) => {
              const paxNameObj = line.metadata?.pax_type_name || line.pax_type_name;
              const paxName = paxNameObj ? t(paxNameObj) : '';
              const displayDesc = paxName ? `${t(line.description)} - ${paxName}` : t(line.description);

              return (
                <tr key={line.id || idx}>
                  <td className="text-center">{line.display_quantity || line.quantity}</td>
                  <td>{displayDesc}</td>
                  <td className="text-center">{getPrice(line.effective_unit_price ?? line.unit_price)}</td>
                  <td className="text-center">{getPrice(line.effective_line_total ?? line.line_total)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" className="text-center">{isAr ? 'لا توجد عناصر' : 'No items'}</td>
            </tr>
          )}

          {/* Subtotal */}
          <tr>
            <td colSpan="2" style={{ border: 'none' }}></td>
            <td className="text-primary font-bold text-center">{isAr ? 'المجموع الفرعي' : 'SUBTOTAL'}</td>
            <td className="font-bold text-center">{getPrice(amounts.subtotal)}</td>
          </tr>

          {/* Discount */}
          <tr>
            <td className="label-cell text-center" style={{ border: '1px solid var(--border-color)' }}>
              {isAr ? 'نسبة الخصم' : 'DISCOUNT %'}
            </td>
            <td className="text-center text-blue font-bold">
              {invoice?.discount_value > 0 ? `${invoice.discount_value}%` : '-'}
            </td>
            <td className="text-red font-bold text-center">{isAr ? 'الخصم' : 'DISCOUNT'}</td>
            <td className="text-red font-bold text-center">{getPrice(discountAmount)}</td>
          </tr>

          {/* Tax */}
          <tr>
            <td className="label-cell text-center" style={{ border: '1px solid var(--border-color)' }}>
              {isAr ? 'نسبة الضريبة' : 'TAX %'}
            </td>
            <td className="text-center text-blue font-bold">
              {invoice?.tax_percentage > 0 ? `${invoice.tax_percentage}%` : '-'}
            </td>
            <td className="text-primary font-bold text-center">{isAr ? 'الضريبة' : 'TAX'}</td>
            <td className="font-bold text-center">{getPrice(taxAmount)}</td>
          </tr>

          {/* Empty space before Total Due */}
          <tr>
            <td colSpan="2" style={{ border: 'none' }}></td>
            <td className="section-header" style={{ border: '1px solid var(--border-color)' }}>
              {isAr ? 'الإجمالي' : 'TOTAL DUE'}
            </td>
            <td className="text-blue font-bold text-center" style={{ fontSize: '18px', whiteSpace: 'nowrap' }}>
              <span style={{ marginInlineEnd: '4px' }}>{currencySymbol}</span>
              {getPrice(amounts.total_due)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Payment Information */}
      <table className="payment-table">
        <tbody>
          <tr>
            <td colSpan="4" className="section-header">{isAr ? 'معلومات الدفع' : 'PAYMENT INFORMATION'}</td>
          </tr>
          <tr>
            <td className="label-cell text-center">{isAr ? 'طريقة الدفع' : 'Payment Method'}</td>
            <td style={{ width: '40%' }}>{invoice?.bank?.id ? (isAr ? 'تحويل بنكي' : 'Bank Transfer') : '-'}</td>
            <td className="label-cell text-center" style={{ width: '20%' }}>{isAr ? 'حالة الدفع' : 'Payment Status'}</td>
            <td className="text-center text-blue font-bold">{paymentStatus}</td>
          </tr>
          <tr>
            <td className="label-cell text-center">{isAr ? 'ملاحظات الدفع' : 'Payment Remark'}</td>
            <td colSpan="3">{t(invoice?.payment_remark) || '-'}</td>
          </tr>
        </tbody>
      </table>

      {/* Bank Details */}
      <table className="bank-table">
        <tbody>
          <tr>
            <td colSpan="2" className="section-header">{isAr ? 'تفاصيل البنك' : 'BANK DETAILS'}</td>
          </tr>
          {invoice?.bank ? (
            <>
              <tr>
                <td className="label-cell">{isAr ? 'اسم البنك' : 'Bank Name'}</td>
                <td>{t(invoice.bank.bank_name) || t(invoice.bank.name) || '-'}</td>
              </tr>
              <tr>
                <td className="label-cell">{isAr ? 'اسم الشركة' : 'Company Name'}</td>
                <td>{t(invoice.bank.company_name) || '-'}</td>
              </tr>
              <tr>
                <td className="label-cell">{isAr ? 'نوع الحساب' : 'Account Type'}</td>
                <td>{isAr ? 'حساب جاري' : 'Current Account'}</td>
              </tr>
              <tr>
                <td className="label-cell">{isAr ? 'رقم الحساب' : 'Account Number'}</td>
                <td>{invoice.bank.account_number || '-'}</td>
              </tr>
              <tr>
                <td className="label-cell">{isAr ? 'الآيبان (IBAN)' : 'IBAN'}</td>
                <td>{invoice.bank.iban || '-'}</td>
              </tr>
              <tr>
                <td className="label-cell">{isAr ? 'العملة' : 'Currency'}</td>
                <td>{invoice.bank.currency?.code || '-'}</td>
              </tr>
            </>
          ) : (
            <tr>
              <td colSpan="2" className="text-center">{isAr ? 'لم يتم إدخال تفاصيل البنك' : 'No bank details provided'}</td>
            </tr>
          )}
        </tbody>
      </table>

      {invoice?.bank?.notes && t(invoice.bank.notes) !== '' && (
        <table>
          <tbody>
            <tr>
              <td className="section-header">{isAr ? 'ملاحظات' : 'NOTES'}</td>
            </tr>
            <tr>
              <td className="notes-content">
                {t(invoice.bank.notes)}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Footer */}
      {/* <div className="thank-you">
        {isAr ? 'شكراً لاختياركم' : 'Thank you for choosing'} <span style={{ color: 'var(--blue)' }}>{isAr ? 'ترافل بلس' : 'TRIP PLUS TRAVEL'}</span>
      </div>
      <div className="company-contacts">
        • Tel: +20 1097 4444 16 • Email: info@tripplustravel.com • Web: www.tripplustravel.com
        <br />
        2 Ibn Ayas Al Masri St., Giza Square, Giza, Egypt
      </div> */}
    </div>
  );
};

export default PrintInvoice;
